// 共通のコンソールページ枠組み
import React, { useMemo, useState } from "react";
import { Alert, Box, Button, Stack, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ConsoleTable from "./ConsoleTable";
import useConsoleData from "../Data/useConsoleData";
import { useToast } from "./ToastProvider";

/**
 * 汎用コンソールページ
 *
 * props:
 * - title: 見出し
 * - plugin: useConsoleData 用プラグイン
 * - columnsBuilder: ({ onEdit }) => columns
 * - renderAddDialog: ({ onClose }) => ReactNode
 * - renderEditDialog: ({ row, onClose }) => ReactNode
 * - searchPlaceholder: 検索欄プレースホルダ
 * - addButtonLabel: 追加ボタンのラベル
 * - initialPageSize: 初期ページサイズ
 * - getRowId: ConsoleTable に渡す rowId 取得関数
 * - searchFieldProps: 検索欄に追加したい props (sx は上書きマージ)
 * - headerExtras: 追加のヘッダー要素（関数 or ReactNode）
 * - toastMessages: { create?, update? } 保存成功時のトースト
 * - onRowClick: 行クリックのハンドラを差し替えたい場合
 */
export default function ConsolePageFrame({
  title,
  plugin,
  columnsBuilder,
  renderAddDialog,
  renderEditDialog,
  searchPlaceholder = "Search",
  addButtonLabel = "Add",
  initialPageSize = 25,
  getRowId,
  searchFieldProps = {},
  headerExtras = null,
  toastMessages = {},
  onRowClick,
  addButtonProps = {},
}) {
  const defaultSort = plugin?.defaultSort || { field: "id", sort: "asc" };

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sortModel, setSortModel] = useState([
    { field: defaultSort.field, sort: defaultSort.sort },
  ]);
  const [search, setSearch] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const [addOpen, setAddOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const toast = useToast();

  const { rows, total, loading, error } = useConsoleData({
    plugin,
    page,
    pageSize,
    sortModel,
    search,
    refreshKey,
  });

  const columns = useMemo(
    () =>
      columnsBuilder
        ? columnsBuilder({ onEdit: setEditRow })
        : plugin?.columns?.({ onEdit: setEditRow }) || [],
    [columnsBuilder, plugin]
  );

  const handleSaved = (type, saved) => {
    if (!saved) return;
    setRefreshKey((k) => k + 1);
    const message = type === "add" ? toastMessages.create : toastMessages.update;
    if (message && toast) {
      toast.push("success", message);
    }
  };

  const effectiveRowClick =
    onRowClick || (renderEditDialog ? (row) => setEditRow(row) : undefined);

  const { sx: searchSx, ...restSearchProps } = searchFieldProps;
  const headerExtraContent =
    typeof headerExtras === "function"
      ? headerExtras({ openAdd: () => setAddOpen(true) })
      : headerExtras;

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ flex: 1 }}>
          {title}
        </Typography>
        <TextField
          size="small"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => {
            setPage(0);
            setSearch(e.target.value);
          }}
          sx={{ minWidth: 280, ...searchSx }}
          {...restSearchProps}
        />
        {headerExtraContent}
        {renderAddDialog && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setAddOpen(true)}
            {...addButtonProps}
          >
            {addButtonLabel}
          </Button>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || "Failed to load data"}
        </Alert>
      )}

      <ConsoleTable
        columns={columns}
        rows={rows}
        total={total}
        page={page}
        onPageChange={(p) => setPage(p)}
        pageSize={pageSize}
        onPageSizeChange={(s) => {
          setPage(0);
          setPageSize(s);
        }}
        sortModel={sortModel}
        onSortModelChange={(m) => {
          setPage(0);
          setSortModel(m);
        }}
        onRowClick={effectiveRowClick}
        loading={loading}
        getRowId={getRowId}
      />

      {addOpen &&
        renderAddDialog?.({
          onClose: (saved) => {
            setAddOpen(false);
            handleSaved("add", saved);
          },
        })}

      {editRow &&
        renderEditDialog?.({
          row: editRow,
          onClose: (saved) => {
            setEditRow(null);
            handleSaved("edit", saved);
          },
        })}
    </Box>
  );
}
